import { Colors, Metrics, Typography } from "../../resources";
import React, { useState } from "react";
import { SIconType, STagType, Tag, TagMap } from "../../types";
import { STagsTextInput, STextButton } from "../atoms";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { has, noop } from "lodash";

import SEditSpaceTagsModal from "../organisms/STagsEditBar";
import SIcon from "../atoms/SIcon";
import STag from "../atoms/STag";
import { getTagName } from "../../utils";

const MORE_CHIP_WIDTH = 12 + 2 * Metrics.padding8 + 20;

interface IProps {
  tags: { [key: string]: Tag };
  type: STagType;
  flexWrap?: boolean;
  hasClearButton?: boolean;
  noShare?: boolean;
  height?: number;
  width?: number | string;
  onEditedAddTag?: (tag: TagMap) => void;
  openPress?: () => void;
  onClick?: (tag: Tag) => void;
  onEdit?: (tag: Tag) => void;
  onClear?: () => void;
  onWidth?: (width: number) => void;
}

const STagsBar: React.FC<IProps> = ({
  tags,
  height = Metrics.size44,
  width = "100%",
  flexWrap = false,
  hasClearButton = false,
  noShare = false,
  type,
  onEditedAddTag = noop,
  openPress = noop,
  onClick = noop,
  onEdit = noop,
  onClear = noop,
  onWidth = noop,
}) => {
  const [visibleTagsCount, setVisibleTagsCount] = useState<number>(
    Object.keys(tags).length
  );
  const [tagSizes, setTagSizes] = useState<{ [key: string]: number }>({});
  const [tagName, setTagName] = useState<string>("");

  const onLayout = (e: any) => {
    const containerWidth = e.nativeEvent.layout.width;
    let tagsWidth = MORE_CHIP_WIDTH;
    let tagsCount = 0;

    for (let tagId of Object.keys(tags).sort()) {
      if (tagsWidth > containerWidth) {
        break;
      }
      tagsWidth = tagsWidth + tagSizes[tagId] + 2 * Metrics.paddingSuperSmall;
      tagsCount++;
    }

    setVisibleTagsCount(tagsCount);
  };

  const sumObjValues = (obj: { [key: string]: number }) => {
    var sum = 0;
    for (var el in obj) {
      if (obj.hasOwnProperty(el)) {
        sum += obj[el];
      }
    }
    return sum;
  };

  const onTagLayout = (id: string, e: any) => {
    let sizes = tagSizes;
    let count = Object.keys(sizes).length;
    sizes[id] = e.nativeEvent.layout.width;
    onWidth(
      sumObjValues(sizes) +
        Metrics.paddingSuperSmall +
        count * (Metrics.padding8 + Metrics.paddingSuperSmall)
    );
    setTagSizes(sizes);
  };

  const onChangeText = (text: string) => {
    const { name, category } = getTagName(text);
    let editedTag: TagMap = {};
    editedTag[text] = {
      title: name,
      category: category,
      number: 0,
      selected: false,
      disabled: false,
      shared: true,
    };
    onEditedAddTag(editedTag);
    setTagName(text);
  };

  const onSubmitEditing = (value: {
    nativeEvent: { text: string; eventCount: number; target: string };
  }) => {
    const { name, category } = getTagName(value.nativeEvent.text);
    onEdit({
      title: name,
      category: category,
      number: 0,
      selected: false,
      disabled: false,
      shared: true,
    });
    setTagName("");
  };

  const renderMoreChip = (count: number) => {
    return (
      <TouchableOpacity
        style={[styles.moreContainer, { width: MORE_CHIP_WIDTH }]}
        onPress={openPress}
      >
        <Text style={[styles.text, { color: Colors.blue700 }]}>{`+${
          count + 1
        }`}</Text>
        <SIcon type={SIconType.ChevronDown} color={Colors.blue700} size={12} />
      </TouchableOpacity>
    );
  };

  return (
    <View
      style={[
        styles.container,
        {
          flexBasis: height,
          width: width,
          flexWrap: flexWrap ? "wrap" : "nowrap",
        },
      ]}
      onLayout={onLayout}
    >
      {Object.keys(tags)
        .sort()
        //        .slice(0, flexWrap ? Object.keys(tags).length : visibleTagsCount - 1)
        .map((id: string) => {
          const item = tags[id];
          return (type === STagType.Closed && item.selected) ||
            type !== STagType.Closed ? (
            <View style={styles.tag} key={`${item.category}-${item.title}`}>
              <STag
                id={id}
                title={item.title}
                type={type}
                number={item.number}
                isSelected={item.selected}
                shared={noShare ? true : item.shared}
                onClick={() => onClick(item)}
                onLayout={onTagLayout}
              />
            </View>
          ) : null;
        })}
      {/* {visibleTagsCount < Object.keys(tags).length && !flexWrap
        ? renderMoreChip(Object.keys(tags).length - visibleTagsCount)
        : null} */}
      {type === STagType.Edited ? (
        <View style={styles.tag}>
          <STagsTextInput
            onChangeText={onChangeText}
            onSubmitEditing={onSubmitEditing}
            value={tagName}
            placeholder="Add tag"
          />
        </View>
      ) : null}
      {hasClearButton &&
      Object.keys(tags).filter((id: string) => tags[id].selected === true)
        .length ? (
        <STextButton label={"Clear Tags"} onPress={onClear} />
      ) : null}
      {type === STagType.Plain && Object.keys(tags).length === 0 ? (
        <View style={styles.tag}>
          <Text style={styles.label}>Add tag</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    paddingVertical: Metrics.padding6 - 1,
  },
  moreContainer: {
    flexDirection: "row",
    backgroundColor: Colors.blue100,
    borderWidth: Metrics.size1,
    borderColor: Colors.blue100,
    height: Metrics.iconSmall,
    justifyContent: "flex-start",
    alignItems: "center",
    paddingLeft: Metrics.padding8,
    paddingRight: Metrics.padding8 / 2,
    borderRadius: Metrics.iconSmall / 2,
    marginVertical: 1,
  },
  tag: {
    marginRight: Metrics.paddingSuperSmall,
  },
  text: {
    ...Typography.small,
    paddingRight: Metrics.paddingSuperSmall,
    textAlign: "center",
    textAlignVertical: "center",
    paddingBottom: 2,
  },
  label: {
    ...Typography.small,
    color: Colors.grey400,
    paddingRight: Metrics.paddingSuperSmall,
    textAlign: "left",
    textAlignVertical: "center",
    paddingBottom: 2,
    flex: 1,
    outlineWidth: 0,
  },
});

export default STagsBar;
